import { getGetDronesUrl } from 'generatedTypes/asset/services/drone-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE } from './constants';
import type { DroneResponse, FetchDrones, FetchDronesParams } from './types';

const useDrones = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchDrones: FetchDrones = async request => {
        try {
            setIsLoading(true);
            const url = getGetDronesUrl(request);

            return await withRetry(
                () => authenticatedGet(url),
                CONNECTION_ERROR,
                () => fetchDrones(request)
            );
        } finally {
            setIsLoading(false);
        }
    };

    const {
        data: dronesData,
        fetch: paginatedFetch,
        reset
    } = usePagination<DroneResponse>({
        callback: fetchDrones
    });

    const handleFetch = (args?: FetchDronesParams) => {
        const { name, pageSize = DEFAULT_PAGE_SIZE, sort } = args || {};

        return paginatedFetch({
            name,
            pageSize,
            sort
        });
    };

    return {
        drones: dronesData as DroneResponse[],
        fetch: handleFetch,
        isLoading,
        reset
    };
};

export default useDrones;
